import { useMemo, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { parseTable, suggestMapping, rowsToTeams, ROLE_LABELS, type ColumnRole, type Mapping, type ParsedTable } from '@airmail/io';
import { Button, Dialog, Select, Textarea, cx } from '@/ui';
import { isSpreadsheet, spreadsheetToText } from '@/lib/spreadsheet';
import { useT } from '@/i18n';
import { useTm } from '@/i18n/engine';
import type { TeamInput } from '@airmail/engine';

const ROLES = Object.keys(ROLE_LABELS) as ColumnRole[];

/**
 * Import-Dialog (docs/05): Einfügen oder Datei → Vorschau mit Spalten-Mapping →
 * Validierung → „Nur gültige importieren“ / „Alle importieren“.
 */
export function ImportDialog({
  open,
  onClose,
  teamSize,
  existingNames,
  onImport,
}: {
  open: boolean;
  onClose: () => void;
  teamSize: number;
  existingNames: string[];
  onImport: (teams: TeamInput[]) => void;
}) {
  const t = useT();
  const tm = useTm();
  const [text, setText] = useState('');
  const [sheets, setSheets] = useState<{ name: string; tsv: string }[] | null>(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [headerOverride, setHeaderOverride] = useState<boolean | null>(null);
  const [mappingOverride, setMappingOverride] = useState<Mapping | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const source = sheets ? (sheets[sheetIdx]?.tsv ?? '') : text;
  const table = useMemo<ParsedTable | null>(() => {
    if (!source.trim()) return null;
    const t = parseTable(source);
    if (headerOverride === null || headerOverride === t.hasHeader) return t;
    // Überschrift manuell umgeschaltet
    if (headerOverride) {
      const first = t.rows[0] ?? t.header;
      return { ...t, hasHeader: true, header: first, rows: t.rows.slice(1) };
    }
    return { ...t, hasHeader: false, header: t.header.map((_, i) => `Spalte ${i + 1}`), rows: [t.header, ...t.rows] };
  }, [source, headerOverride]);

  const mapping = useMemo<Mapping>(() => {
    if (!table) return [];
    if (mappingOverride && mappingOverride.length === table.header.length) return mappingOverride;
    return suggestMapping(table);
  }, [table, mappingOverride]);

  const result = useMemo(() => (table ? rowsToTeams(table, mapping, { teamSize, existingNames }) : null), [table, mapping, teamSize, existingNames]);

  const reset = () => {
    setText('');
    setSheets(null);
    setSheetIdx(0);
    setHeaderOverride(null);
    setMappingOverride(null);
    setFileError(null);
  };
  const close = () => {
    reset();
    onClose();
  };

  const onFile = async (file: File) => {
    setFileError(null);
    try {
      if (isSpreadsheet(file)) {
        const r = await spreadsheetToText(file);
        if (r.sheets.length === 0) return setFileError(t('Keine Tabellenblätter gefunden'));
        setSheets(r.sheets);
        setSheetIdx(r.sheets.findIndex((s) => s.tsv.trim() !== '') || 0);
      } else {
        setSheets(null);
        setText(await file.text());
      }
      setMappingOverride(null);
      setHeaderOverride(null);
    } catch (e) {
      setFileError(t('Datei konnte nicht gelesen werden: {e}', { e: (e as Error).message }));
    }
  };

  const doImport = (onlyValid: boolean) => {
    if (!result) return;
    const teams = result.teams.filter((t) => !t.issues.some((i) => i.level === 'error') && (!onlyValid || t.issues.length === 0)).map((t) => t.team);
    onImport(teams);
    close();
  };

  const hasRoleTwice = ROLES.filter((r) => r !== 'ignore').some((r) => mapping.filter((m) => m === r).length > 1);

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t('Teams importieren')}
      width="max-w-5xl"
      footer={
        <>
          <div className="text-sm text-muted flex-1">
            {result && (
              <>
                <b className="text-ink">{t('{n} Teams', { n: result.summary.total })}</b>
                {result.summary.withWarnings > 0 && <span className="text-warn"> · {t('{n} mit Hinweisen', { n: result.summary.withWarnings })}</span>}
                {result.summary.withErrors > 0 && <span className="text-err"> · {t('{n} nicht importierbar', { n: result.summary.withErrors })}</span>}
                {result.issues.map((i) => (
                  <span key={i.message} className="text-warn">
                    {' '}
                    · {i.message}
                  </span>
                ))}
              </>
            )}
          </div>
          <Button variant="ghost" onClick={close}>
            {t('Abbrechen')}
          </Button>
          {result && result.summary.withWarnings > 0 && (
            <Button onClick={() => doImport(true)} disabled={hasRoleTwice}>
              {t('Nur Zeilen ohne Hinweise ({n})', { n: result.summary.total - result.summary.withWarnings - result.summary.withErrors })}
            </Button>
          )}
          <Button variant="primary" onClick={() => doImport(false)} disabled={!result || result.summary.valid === 0 || hasRoleTwice}>
            {result ? t('{n} Teams importieren', { n: result.summary.valid }) : t('Importieren')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start">
          <Textarea
            rows={sheets ? 3 : 7}
            value={sheets ? t('Datei geladen: {n} Blatt/Blätter', { n: sheets.length }) : text}
            readOnly={!!sheets}
            onChange={(e) => {
              setText(e.target.value);
              setMappingOverride(null);
              setHeaderOverride(null);
            }}
            placeholder={t('Aus Excel/Sheets kopieren und hier einfügen — oder tippen:\nBag Brothers\tDaniel Roth\tMax Müller\nLisa Schmitt / Tom Berger')}
            onPaste={() => setSheets(null)}
          />
          <div className="grid gap-2">
            <Button onClick={() => fileRef.current?.click()}>
              <Upload size={14} /> {t('Datei (.xlsx, .csv)')}
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls,.ods,.csv,.tsv,.txt" hidden onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
            {sheets && sheets.length > 1 && (
              <Select value={sheetIdx} onChange={(e) => setSheetIdx(Number(e.target.value))} aria-label={t('Tabellenblatt')}>
                {sheets.map((s, i) => (
                  <option key={s.name} value={i}>
                    {t('Blatt: {name}', { name: s.name })}
                  </option>
                ))}
              </Select>
            )}
            {sheets && (
              <Button variant="ghost" size="sm" onClick={reset}>
                {t('Datei verwerfen')}
              </Button>
            )}
            {fileError && <div className="text-err text-xs">{fileError}</div>}
          </div>
        </div>

        {table && result && (
          <>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={table.hasHeader} onChange={(e) => setHeaderOverride(e.target.checked)} />
                {t('Erste Zeile ist Überschrift')}
              </label>
              <span className="text-dim font-mono text-[11px]">
                {t('Trenner:')} {table.delimiter === '\t' ? 'Tab' : table.delimiter}
              </span>
              {hasRoleTwice && <span className="text-err">{t('Eine Rolle ist zwei Spalten zugeordnet')}</span>}
              <span className="ml-auto text-dim text-xs">{t('Zuordnung über jeder Spalte prüfen und ggf. ändern')}</span>
            </div>
            <div className="overflow-auto border border-line max-h-[44vh]">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-panel">
                  <tr>
                    <th className="px-2 py-1.5 text-left label w-8">#</th>
                    {table.header.map((h, i) => (
                      <th key={i} className="px-2 py-1.5 text-left font-normal align-top">
                        <Select
                          aria-label={t('Spalte {n} zuordnen', { n: i + 1 })}
                          className={cx('text-xs py-1', mapping[i] === 'ignore' && 'text-dim')}
                          value={mapping[i]}
                          onChange={(e) => {
                            const m = mapping.slice();
                            m[i] = e.target.value as ColumnRole;
                            setMappingOverride(m);
                          }}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {t(ROLE_LABELS[r])}
                            </option>
                          ))}
                        </Select>
                        <div className="label mt-1 truncate max-w-[160px]" title={table.hasHeader ? h : t('Spalte {n}', { n: i + 1 })}>
                          {table.hasHeader ? h : t('Spalte {n}', { n: i + 1 })}
                        </div>
                      </th>
                    ))}
                    <th className="px-2 py-1.5 text-left label">{t('Hinweise')}</th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, ri) => {
                    const team = result.teams.find((t) => t.rows.includes(ri));
                    const issues = team?.rows[0] === ri ? (team?.issues ?? []) : [];
                    const level = issues.some((i) => i.level === 'error') ? 'error' : issues.length ? 'warn' : 'ok';
                    return (
                      <tr key={ri} className={cx('border-t border-line2', level === 'error' && 'bg-red/10', level === 'warn' && 'bg-warn/10')}>
                        <td className="px-2 py-1 font-mono text-[11px] text-dim">{ri + 1}</td>
                        {row.map((c, ci) => (
                          <td key={ci} className={cx('px-2 py-1 whitespace-nowrap', mapping[ci] === 'ignore' && 'text-dim')}>
                            {c}
                          </td>
                        ))}
                        <td className="px-2 py-1 text-xs">
                          {issues.map((i) => (
                            <div key={i.message} className={i.level === 'error' ? 'text-err' : 'text-warn'}>
                              {tm(i.message)}
                            </div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
